"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.heldCallOfferSchema = exports.HOLD_ACTIONS = exports.heldCallAlertParamsSchema = exports.heldCallCloseInputSchema = exports.heldCallParamsSchema = exports.heldCallOpenedSchema = exports.heldCallOpenInputSchema = exports.heldCallListSchema = exports.heldCallListQuerySchema = exports.heldCallSchema = exports.heldCallResidentSchema = void 0;
/**
 * `RPM-039` — held and batched calls (`J-228`).
 *
 * One outstanding phone contact may cover several critical entries for the same number. A batch is
 * keyed by the NUMBER, not the building (`CR-10`): at most one open batch per nurse per number, and
 * the database's partial unique index is what makes that true. The due time never moves once set.
 *
 * The block's API row: `POST /api/rpm/v1/held-calls` (open or join) and
 * `DELETE /api/rpm/v1/held-calls/:id/alerts/:alertId` (take one resident off the call). The reads the
 * panel needs and the call being made (`SM-BATCH/PENDING--begin_attempt`) are this file's too, and
 * so are `RPM-043`'s two: a resident marked reached, and `POST /api/rpm/v1/held-calls/:id/close`
 * with the terminal outcome (`J-231`).
 *
 * NUMBERS ARE THE BUILDING'S CONFIGURATION AND TRAVEL IN BODIES ONLY, never in a path or a query
 * string, as the routing's recipients do (`RPM-070`).
 */
const zod_1 = require("zod");
const rpm_enums_js_1 = require("./db/rpm-enums.js");
/** The mirror's identifier width. `carease.facilities.id` is varchar(20), and no FK reaches it. */
const MIRROR_ID = zod_1.z.string().min(1).max(20);
/** One resident a batch covers: a live attachment (`held_call_alerts`, `detached_at` null). */
exports.heldCallResidentSchema = zod_1.z.object({
    rpmAlertId: zod_1.z.uuid(),
    careasePatientId: MIRROR_ID,
    residentName: zod_1.z.string().nullable(),
    attachedAt: zod_1.z.iso.datetime({ offset: true }),
    /**
     * Whether the batch's call reached this resident (`held_call_alerts.reached`, `RPM-043`). The batch
     * closes only when every resident on it is reached.
     */
    reached: zod_1.z.boolean(),
});
exports.heldCallSchema = zod_1.z
    .object({
    heldCallId: zod_1.z.uuid(),
    facilityId: MIRROR_ID,
    facilityName: zod_1.z.string().nullable(),
    /** The batch key: the number as the routing lists it, verbatim (`CR-10`). */
    destinationValue: zod_1.z.string(),
    destinationLabel: zod_1.z.string().nullable(),
    /** The care unit the batch is for, where it is for one. Snapshotted when it opened. */
    locationText: zod_1.z.string().nullable(),
    /** The nurse holding it. Recorded, and kept when her reach ends (`RPM-094` row 4). */
    owner: zod_1.z.object({ userId: zod_1.z.string(), name: zod_1.z.string().nullable() }),
    openedAt: zod_1.z.iso.datetime({ offset: true }),
    /** `opened_at` plus the hold window. Never moves, whoever joins (`CR-10`). */
    dueAt: zod_1.z.iso.datetime({ offset: true }),
    holdWindowMinutes: zod_1.z.number().int(),
    batchState: zod_1.z.enum(rpm_enums_js_1.batchState.enumValues),
    unansweredAttemptCount: zod_1.z.number().int(),
    /** Why the batch closed with no outcome: the last resident was taken off it. */
    cancelReason: zod_1.z.string().nullable(),
    residents: zod_1.z.array(exports.heldCallResidentSchema),
})
    .meta({ id: "HeldCall", description: "One held call: its number, due time and residents" });
exports.heldCallListQuerySchema = zod_1.z
    .strictObject({
    /** Without one, every building the account can reach. */
    facilityId: MIRROR_ID.optional(),
})
    .meta({ description: "The open held calls in the buildings the account reaches" });
exports.heldCallListSchema = zod_1.z
    .object({ items: zod_1.z.array(exports.heldCallSchema) })
    .meta({ description: "The open held calls, soonest due first" });
/** Open a batch for this entry's call, or join the one open for the number (`RPM-039`). */
exports.heldCallOpenInputSchema = zod_1.z
    .strictObject({
    rpmAlertId: zod_1.z.uuid(),
    /** A number the entry's call step lists, as the routing lists it. */
    destinationValue: zod_1.z
        .string()
        .max(320)
        .refine((v) => v.trim() !== "", { message: "Say which number the call is held for." }),
    /**
     * The batch she chose to join, where more than one was open (`DT-HOLD-ELIGIBILITY` R7). A batch
     * that closed meanwhile is refused, "that call is finished", and the entry keeps its own call.
     */
    heldCallId: zod_1.z.uuid().optional(),
})
    .meta({
    description: "Hold this entry's call: open a batch for the number or join the open one",
});
exports.heldCallOpenedSchema = zod_1.z
    .object({
    heldCall: exports.heldCallSchema,
    /** True where the entry joined a batch already open for the number. */
    joined: zod_1.z.boolean(),
})
    .meta({ description: "The batch the entry is now on, and whether it joined one already open" });
exports.heldCallParamsSchema = zod_1.z.strictObject({ heldCallId: zod_1.z.uuid() });
/**
 * Close the batch with its terminal outcome (`RPM-043`, `SM-BATCH/ATTEMPTING--close_all_reached`).
 * A no answer never closes a batch (`CR-10`), and a held call is a phone call (`na_held_is_phone`),
 * so the one outcome a batch closes with is `REACHED`: anything else is refused at the boundary.
 */
exports.heldCallCloseInputSchema = zod_1.z
    .strictObject({
    outcome: zod_1.z.enum(["REACHED"]),
    /** Who was spoken to, as she records it; written on every resident's line. */
    contactPersonText: zod_1.z.string().max(200).optional(),
})
    .meta({ description: "Close a held call once it reached every resident on it" });
exports.heldCallAlertParamsSchema = zod_1.z.strictObject({
    heldCallId: zod_1.z.uuid(),
    alertId: zod_1.z.uuid(),
});
/** `DT-HOLD-ELIGIBILITY`'s four answers (`02-RPM-WORKFLOW.md` §7.17). */
exports.HOLD_ACTIONS = [
    "not-offered",
    "offer-open-new",
    "join-automatically",
    "ask-which-to-join",
];
/** Whether this entry's call may be held, and on which batch (`DT-HOLD-ELIGIBILITY`). */
exports.heldCallOfferSchema = zod_1.z
    .object({
    /** The decision table's rule that decided it, `R1` to `R7`. */
    rule: zod_1.z.enum(["R1", "R2", "R3", "R4", "R5", "R6", "R7"]),
    action: zod_1.z.enum(exports.HOLD_ACTIONS),
    /** Why the hold is not offered, in the table's words; null where it is. */
    reason: zod_1.z.string().nullable(),
    /** The numbers the entry's call step lists, reachable now. */
    numbers: zod_1.z.array(zod_1.z.object({ value: zod_1.z.string(), label: zod_1.z.string() })),
    /** Her open batches to one of those numbers: the ones this entry may join. */
    batches: zod_1.z.array(exports.heldCallSchema),
    /** The batch this entry is on now, open; null where it is on none. */
    heldOn: exports.heldCallSchema.nullable(),
})
    .meta({ id: "HeldCallOffer", description: "Whether this entry's call may be held, and where" });
//# sourceMappingURL=rpm-held-calls.js.map